const TOKEN_OVERLAP_THRESHOLD = 0.8;
const LEVENSHTEIN_SIMILARITY_THRESHOLD = 0.85;
const MIN_SUBSTRING_LENGTH = 8;
const MIN_SUBSTRING_TOKENS = 2;
const MIN_CORE_TOKEN_COUNT = 2;

const BUSINESS_SUFFIXES = new Set([
  'llc',
  'inc',
  'corp',
  'ltd',
  'co',
  'company',
  'pllc',
  'lp'
]);

const stripBusinessSuffixes = (value) =>
  value
    .split(' ')
    .filter((token) => token && !BUSINESS_SUFFIXES.has(token))
    .join(' ');

export const normalizeBusinessName = (name) => {
  if (!name) return '';

  let normalized = name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[,.'"()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  normalized = stripBusinessSuffixes(normalized);

  return normalized.replace(/\s+/g, ' ').trim();
};

const getComparableNameVariants = (name) => {
  const normalized = normalizeBusinessName(name);
  if (!normalized) return [];

  const variants = new Set([normalized]);
  const dbaMatch = normalized.match(/^(.+?)\s+dba\s+(.+)$/);

  if (dbaMatch) {
    const beforeDba = stripBusinessSuffixes(dbaMatch[1].trim());
    const afterDba = stripBusinessSuffixes(dbaMatch[2].trim());
    if (beforeDba) variants.add(beforeDba);
    if (afterDba) variants.add(afterDba);
  }

  return Array.from(variants);
};

const getTokens = (value) =>
  value.split(' ').filter(Boolean);

const hasMeaningfulSubstringMatch = (left, right) => {
  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  if (!shorter || !longer || shorter === longer) return false;

  const shorterTokens = getTokens(shorter);
  const meetsMinimum =
    shorter.length >= MIN_SUBSTRING_LENGTH || shorterTokens.length >= MIN_SUBSTRING_TOKENS;

  return meetsMinimum && longer.includes(shorter);
};

const hasCoreTokenContainment = (left, right) => {
  const leftTokens = getTokens(left);
  const rightTokens = new Set(getTokens(right));

  const [shorterTokens, longerTokens] =
    leftTokens.length <= rightTokens.size ? [leftTokens, rightTokens] : [getTokens(right), new Set(leftTokens)];

  if (shorterTokens.length < MIN_CORE_TOKEN_COUNT) return false;

  return shorterTokens.every((token) => longerTokens.has(token));
};

const tokenOverlapScore = (left, right) => {
  const leftTokens = new Set(getTokens(left));
  const rightTokens = new Set(getTokens(right));

  if (leftTokens.size === 0 || rightTokens.size === 0) return 0;

  let intersection = 0;
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) intersection += 1;
  });

  const union = new Set([...leftTokens, ...rightTokens]).size;
  return union === 0 ? 0 : intersection / union;
};

const levenshteinDistance = (left, right) => {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const matrix = Array.from({ length: left.length + 1 }, () =>
    Array(right.length + 1).fill(0)
  );

  for (let i = 0; i <= left.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= right.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[left.length][right.length];
};

const levenshteinSimilarity = (left, right) => {
  if (!left && !right) return 1;
  if (!left || !right) return 0;

  const distance = levenshteinDistance(left, right);
  const maxLength = Math.max(left.length, right.length);
  return maxLength === 0 ? 1 : 1 - distance / maxLength;
};

const variantsAreSimilarEnough = (leftVariants, rightVariants) => {
  for (const left of leftVariants) {
    for (const right of rightVariants) {
      if (left === right) return true;
      if (hasMeaningfulSubstringMatch(left, right)) return true;
      if (hasCoreTokenContainment(left, right)) return true;
      if (tokenOverlapScore(left, right) >= TOKEN_OVERLAP_THRESHOLD) return true;
      if (levenshteinSimilarity(left, right) >= LEVENSHTEIN_SIMILARITY_THRESHOLD) return true;
    }
  }

  return false;
};

export const namesAreSimilarEnough = (nameA, nameB) => {
  const normalizedA = normalizeBusinessName(nameA);
  const normalizedB = normalizeBusinessName(nameB);

  if (!normalizedA && !normalizedB) return true;
  if (!normalizedA || !normalizedB) return false;

  const variantsA = getComparableNameVariants(nameA);
  const variantsB = getComparableNameVariants(nameB);

  return variantsAreSimilarEnough(variantsA, variantsB);
};
